import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Search, HelpCircle, BookOpen, Video, DollarSign, Settings, MessageCircle } from "lucide-react";
import { useState } from "react";

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "HiStage Help Center",
    "description": "Get help with HiStage. Browse FAQs, guides, and tutorials for creators and fans.",
    "url": "https://histage.lovable.app/help"
  };

  const categories = [
    {
      icon: Video,
      title: "Getting Started",
      desc: "Learn the basics of HiStage",
      articles: 12
    },
    {
      icon: DollarSign,
      title: "Payments & Earnings",
      desc: "Tipping, payouts, and wallet",
      articles: 8
    },
    {
      icon: Settings,
      title: "Account Settings",
      desc: "Manage your profile and preferences",
      articles: 10
    },
    {
      icon: BookOpen,
      title: "Creator Guide",
      desc: "Tips for successful streaming",
      articles: 15
    }
  ];

  const faqs = [
    {
      question: "How do I start my first live stream?",
      answer: "Navigate to your Creator Dashboard and click 'Go Live'. Set up your stream details, configure your camera and microphone, then click 'Start Broadcasting'."
    },
    {
      question: "How does tipping work?",
      answer: "Fans can send tips during live streams using StagCoins. Tips support creators directly and can unlock exclusive content when goal thresholds are reached."
    },
    {
      question: "When do I receive my earnings?",
      answer: "Earnings are processed weekly and transferred to your connected payment method. You need a minimum balance of $50 to request a payout."
    },
    {
      question: "Can I schedule streams in advance?",
      answer: "Yes! Use the scheduling feature in your Creator Dashboard to set up future streams and notify your followers automatically."
    },
    {
      question: "What are the technical requirements?",
      answer: "You'll need a stable internet connection (minimum 5 Mbps upload), a webcam, and a modern browser (Chrome, Firefox, or Safari)."
    },
    {
      question: "How do I become a creator?",
      answer: "Sign up for a HiStage account and apply for creator status in your profile settings. Approval typically takes 24-48 hours."
    }
  ];

  return (
    <>
      <SEO 
        title="HiStage Help Center - Support & FAQs"
        description="Find answers to common questions about HiStage. Get help with streaming, payments, account settings, and more. Browse guides and tutorials for creators and fans."
        url="/help"
        keywords={[
          "histage help",
          "streaming support",
          "faq",
          "how to stream",
          "creator guide",
          "payment help",
          "account settings"
        ]}
        schema={schema}
      />
      
      <main className="min-h-screen bg-background">
        {/* Hero with Search */}
        <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                How can we help you?
              </h1>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search for help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-full border border-input bg-background text-foreground text-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-foreground mb-8 text-center">
                Browse by Category
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {categories.map((category, i) => (
                  <div key={i} className="clean-card rounded-2xl p-6 hover-lift cursor-pointer">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <category.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{category.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{category.desc}</p>
                    <p className="text-xs text-muted-foreground">{category.articles} articles</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-muted-foreground">
                  Quick answers to common questions
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div key={i} className="clean-card rounded-2xl p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <HelpCircle className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">{faq.question}</h3>
                        <p className="text-muted-foreground text-sm">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="clean-card rounded-2xl p-12 max-w-3xl mx-auto text-center">
              <MessageCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Still need help?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Our support team is here to assist you
              </p>
              <Link to="/contact">
                <Button size="lg" className="rounded-full px-8">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default HelpCenter;
